// Nov izobraževalno-prodajni članek za potencialne najemnike prostora na kupish.si.
// Tema: stroški postavitve lastne spletne trgovine v primerjavi z najemom prostora.

export const stroskiVsNajemArticle = {
  slug: "stroski-spletne-trgovine-vs-najem-prostora",
  title: "Stroški spletne trgovine ali najem prostora: kaj se bolj splača slovenskemu prodajalcu",
  section: "unikatne-vsebine" as const,
  date: "2026-09-03 09:00:00",
  excerpt:
    "Realen izračun stroškov lastne spletne trgovine (postavitev, gostovanje, vzdrževanje, oglasi) v primerjavi z najemom prostora na kupish.si — s primeri, tabelo in nasveti za prve kupce.",
  content: `
<p>Odločitev je videti preprosta: <strong>postaviti svojo spletno trgovino</strong> ali <strong>najeti prostor na že delujoči platformi</strong>. V praksi je odločitev finančna, časovna in strateška. Ta vodnik razčleni vsak strošek posebej, pokaže, kje se skrivajo prikriti izdatki, in vam pomaga izračunati, kdaj se katera pot resnično splača.</p>

<p>Namenjen je manjšim trgovcem, obrtnikom, uvoznikom in trgovinam, ki že prodajajo v fizični poslovalnici in razmišljajo o spletni prodaji — brez tveganja, da bodo prvo leto porabili več, kot bodo zaslužili.</p>

<h2>Zakaj je vstop v spletno prodajo dražji, kot se zdi</h2>

<p>Večina izračunov se ustavi pri ceni izdelave spletne trgovine. To je le vstopnica. Prava cena spletne prodaje je sestavljena iz štirih sklopov, ki tečejo <strong>vsak mesec, vsako leto</strong>:</p>

<ul>
<li><strong>Tehnika:</strong> izdelava, gostovanje, domena, varnostni certifikat, varnostne kopije, posodobitve.</li>
<li><strong>Vsebina:</strong> fotografije, opisi izdelkov, prevodi, kategorije, prilagajanje cen in zalog.</li>
<li><strong>Promet:</strong> oglaševanje, SEO, e-poštni marketing, družbena omrežja.</li>
<li><strong>Podpora in pravni okvir:</strong> pogoji poslovanja, obveščanje o odstopu od nakupa, reklamacije, varstvo osebnih podatkov, računovodstvo.</li>
</ul>

<p>Prav zadnja dva sklopa najpogosteje pokopljeta projekt. Trgovina je narejena, izdelki so notri — a je nihče ne obišče. Trgovina brez obiska je enaka lokalu v industrijski coni brez table na cesti.</p>

<h2>Korak za korakom: kaj vas čaka pri lastni spletni trgovini</h2>

<h3>1. Izbira platforme in izdelava</h3>

<p>Izbirate lahko med odprtokodno rešitvijo (najpogosteje WooCommerce), naročniško platformo ali povsem prilagojeno izdelavo. Odprtokodna pot je videti najcenejša, dokler ne prištejete dela.</p>

<ul>
<li>Preprosta trgovina z osnovno predlogo: nekaj dni dela, če imate izkušnje.</li>
<li>Trgovina z več kategorijami, filtri, plačilnimi načini in dostavo: praviloma nekaj tednov.</li>
<li>Prilagojena izdelava pri agenciji: običajno najdražja postavka celotnega projekta.</li>
</ul>

<p><strong>Pogosta napaka:</strong> nakup poceni predloge, ki jo je treba pozneje popolnoma predelati. Predloga, ki ni pripravljena za mobilne naprave in hitro nalaganje, vas bo stala več kot dražja, dobro vzdrževana rešitev.</p>

<h3>2. Gostovanje, domena in varnost</h3>

<p>Spletna trgovina potrebuje zmogljivejše gostovanje kot predstavitvena stran. Poleg tega potrebujete domeno, SSL certifikat, samodejne varnostne kopije in nadzor nad posodobitvami.</p>

<ul>
<li>Gostovanje je letni strošek, ki raste z obiskom in številom izdelkov.</li>
<li>Varnostne kopije morajo biti zunaj strežnika, sicer ob okvari ne pomagajo.</li>
<li>Posodobitve vtičnikov je treba preizkusiti — vsaka lahko podre blagajno.</li>
</ul>

<p><strong>Opozorilo:</strong> nedelujoča blagajna med praznično sezono ni tehnična nadloga, temveč izpad prihodka v najboljših dneh leta.</p>

<h3>3. Plačila in dostava</h3>

<p>Potrebujete vsaj eno kartično plačilo, praviloma pa tudi plačilo po povzetju in nakazilo na račun. Vsak ponudnik plačil zaračuna provizijo od transakcije, nekateri tudi mesečno naročnino.</p>

<p>Pri dostavi se pogajate s ponudniki logistike sami. Manjši trgovec brez volumna dobi slabše cene kot večji, kar neposredno vpliva na ceno izdelka in konkurenčnost.</p>

<h3>4. Fotografije in opisi izdelkov</h3>

<p>To je delo, ki ga ni mogoče preskočiti in ga pogosto podcenimo. Za vsak izdelek potrebujete:</p>

<ul>
<li>vsaj tri fotografije (glavna, detajl, uporaba v prostoru),</li>
<li>naslov, ki vsebuje izraz, ki ga kupci dejansko iščejo,</li>
<li>opis z merami, materiali, vzdrževanjem in rokom dobave,</li>
<li>pravilno uvrstitev v kategorijo in podkategorijo.</li>
</ul>

<p>Če imate 100 izdelkov, je to 100 kompletov vsebine. Pri desetih minutah na izdelek je to več kot dva delovna dneva — in to samo za prvi vnos, brez posodabljanja.</p>

<h3>5. Promet: največji in najbolj podcenjen strošek</h3>

<p>Nova domena nima zgodovine, povratnih povezav ne ugleda pri iskalnikih. Prvi obisk zato pride iz oglasov ali iz vsebine, ki jo pišete mesece vnaprej.</p>

<ul>
<li><strong>Plačani oglasi:</strong> rezultat je takojšen, a se ustavi v trenutku, ko izklopite proračun.</li>
<li><strong>SEO:</strong> deluje dolgoročno, a prvi resni rezultati praviloma pridejo po nekaj mesecih rednega dela.</li>
<li><strong>Družbena omrežja:</strong> zahtevajo stalno objavljanje in odzivanje.</li>
</ul>

<p><strong>Pogosta napaka:</strong> celoten proračun porabiti za izdelavo trgovine in nič ne pustiti za promet. Rezultat je lepa trgovina brez kupcev.</p>

<h3>6. Zaupanje kupca</h3>

<p>Slovenski kupec je previden. Pri neznani trgovini preveri, ali obstajajo podatki o podjetju, pogoji poslovanja, možnost vračila in kontakt s človekom. Nova, samostojna trgovina mora zaupanje graditi od ničle — z vsakim posameznim obiskovalcem posebej.</p>

<h2>Primerjava: lastna trgovina proti najemu prostora na kupish.si</h2>

<table class="article-comparison-table">
<thead>
<tr><th>Področje</th><th>Lastna spletna trgovina</th><th>Najem prostora na kupish.si</th></tr>
</thead>
<tbody>
<tr><td>Začetni vložek</td><td>Izdelava, predloga, nastavitve, testiranje</td><td>Letna najemnina, brez izdelave</td></tr>
<tr><td>Čas do prve prodaje</td><td>Tedni do meseci</td><td>Dnevi</td></tr>
<tr><td>Gostovanje in varnost</td><td>Vaša odgovornost in strošek</td><td>Vključeno</td></tr>
<tr><td>Posodobitve in okvare</td><td>Vaš čas ali plačan razvijalec</td><td>Vključeno</td></tr>
<tr><td>Obisk</td><td>Gradite od ničle</td><td>Obstoječi obisk platforme</td></tr>
<tr><td>Zaupanje kupcev</td><td>Gradite sami</td><td>Skupna blagovna znamka in pravila platforme</td></tr>
<tr><td>Vaš fokus</td><td>Tehnika, marketing, prodaja</td><td>Izdelki in prodaja</td></tr>
<tr><td>Nadzor nad videzom</td><td>Popoln</td><td>Znotraj okvira platforme</td></tr>
</tbody>
</table>

<p>Primerjava ni črno-bela. Lastna trgovina je smiselna, kadar imate stabilen obseg naročil, lasten marketinški proračun in nekoga, ki skrbi za tehniko. Najem prostora je smiseln, kadar želite <strong>prodajati zdaj</strong> in preveriti, kateri izdelki se dejansko prodajajo, preden vložite v lastno infrastrukturo.</p>

<h2>Kako izračunate, kaj se splača prav vam</h2>

<p>Namesto ugibanja uporabite preprost izračun. Zapišite si tri številke:</p>

<ol>
<li><strong>Letni strošek poti A</strong> (izdelava, gostovanje, vzdrževanje, oglasi, vaš čas po realni urni postavki).</li>
<li><strong>Letni strošek poti B</strong> (najemnina prostora, vaš čas za urejanje izdelkov).</li>
<li><strong>Pričakovana letna marža</strong> (število naročil × povprečna vrednost naročila × delež marže).</li>
</ol>

<p>Če pričakovana marža ne pokrije poti A že v prvem letu, začnite s potjo B. Ko se prodaja ustali in poznate svoje najboljše izdelke, je prehod na lastno trgovino veliko manj tvegan — takrat namreč veste, kaj se prodaja in komu.</p>

<h3>Praktičen primer</h3>

<p>Trgovec ima 60 izdelkov s povprečno ceno 35 EUR in maržo 30 %. Za pokritje zgolj tehničnih stroškov lastne trgovine mora prodati precejšnje število izdelkov, še preden zasluži prvi evro. Če isti trgovec začne z najemom prostora, njegov prag rentabilnosti pade na nekaj naročil mesečno — preostanek proračuna pa lahko usmeri v fotografije in opise, ki dejansko prodajajo.</p>

<h2>Kako kupish.si odpravi glavne ovire</h2>

<p><a class="decorated-link" href="/najem-spletne-trgovine">kupish.si</a> je slovensko nakupovalno središče novih izdelkov različnih ponudnikov in spletnih trgovin. Namesto da postavljate lastno infrastrukturo, najamete prostor v že delujočem sistemu.</p>

<h3>Hitrost</h3>
<p>Registracija, plačilo najemnine, vnos izdelkov — trgovina je lahko na spletu v nekaj dneh. Brez izbire predloge, brez čakanja na razvijalca.</p>

<h3>Brez tehničnih stroškov</h3>
<p>Gostovanje, varnostni certifikat, varnostne kopije, posodobitve in delovanje blagajne so del platforme. Ne plačujete jih posebej in ne skrbite zanje.</p>

<h3>Obstoječi obisk</h3>
<p>Kupci, ki že pridejo na kupish.si prek iskalnikov in kategorij, vidijo tudi vaše izdelke. Vaša naloga je, da so izdelki dobro fotografirani in pravilno opisani — obisk platforme je skupen.</p>

<h3>Varnost in urejeno poslovanje</h3>
<p>Pravni okvir, pogoji poslovanja in postopek reklamacij so vzpostavljeni. Kupec vidi urejeno okolje, kar zniža njegov odpor do prvega nakupa pri prodajalcu, ki ga še ne pozna.</p>

<h3>Vaš čas ostane pri prodaji</h3>
<p>Namesto reševanja tehničnih težav urejate ponudbo, cene, zaloge in odgovarjate strankam. Prav to so opravila, ki prinašajo prihodek.</p>

<h2>Kaj naredite v prvih 30 dneh</h2>

<ul>
<li><strong>1.–3. dan:</strong> registracija in oddaja podatkov o trgovini prek strani <a class="decorated-link" href="/najem-spletne-trgovine">najem spletne trgovine</a>.</li>
<li><strong>4.–10. dan:</strong> priprava fotografij in opisov za prvih 20 izdelkov — začnite s tistimi, ki se v fizični prodaji najbolje prodajajo.</li>
<li><strong>11.–20. dan:</strong> objava izdelkov, ureditev kategorij, preverjanje cen dostave.</li>
<li><strong>21.–30. dan:</strong> obveščanje obstoječih strank, objava povezav na družbenih omrežjih, dopolnitev opisov na podlagi prvih vprašanj kupcev.</li>
</ul>

<p>Praktična navodila za vsak korak najdete v <a class="decorated-link" href="/uporabniska-navodila-ustvarjalci">navodilih za prodajalce</a>, odgovore na najpogostejša vprašanja pa v razdelku <a class="decorated-link" href="/pogosta-vprasanja">pogosta vprašanja</a>.</p>

<h2>Najpogostejše napake, ki jih ne ponavljajte</h2>

<ul>
<li><strong>Preveč izdelkov naenkrat, prehitro.</strong> Bolje 20 odlično predstavljenih kot 200 na hitro vnesenih.</li>
<li><strong>Fotografije s telefona v slabi svetlobi.</strong> Naravna svetloba in enotno ozadje naredita več kot draga oprema.</li>
<li><strong>Opisi, prepisani od dobavitelja.</strong> Enak opis kot pri desetih drugih prodajalcih ne prinese uvrstitve v iskalniku.</li>
<li><strong>Manjkajoči podatki o dostavi in vračilu.</strong> Kupec, ki ne najde odgovora, ne vpraša — odide.</li>
<li><strong>Odsotnost po objavi.</strong> Na povpraševanja odgovarjajte isti delovni dan.</li>
</ul>

<h2>Skriti strošek, ki ga nihče ne zaračuna: vaš čas</h2>

<p>Ura, ki jo porabite za posodabljanje vtičnika ali iskanje vzroka za napako na blagajni, je ura, ki je niste porabili za nabavo, prodajo ali stranke. Če svoj čas ovrednotite z realno urno postavko, se razmerje med potjo A in potjo B pogosto obrne že v prvem letu.</p>

<p>Pri tem velja preprosto pravilo: <strong>vsako opravilo, ki ne prinaša prihodka in ga lahko prevzame platforma, naj ga prevzame platforma</strong>. Vaša konkurenčna prednost je poznavanje izdelkov in kupcev, ne administracija strežnika.</p>

<h3>Sezona ne čaka</h3>

<p>Prodajni vrhovi v Sloveniji so predvidljivi: šolska sezona, črni petek, december, valentinovo, materinski dan. Če trgovino postavljate pol leta, boste zamudili vsaj enega. Najem prostora vam omogoči, da se vključite v sezono, ki je pred vami, ne v tisto čez leto dni.</p>

<h2>Pogosta vprašanja</h2>


<h3>Ali potrebujem lastno domeno, če najamem prostor?</h3>
<p>Ne. Vaša trgovina deluje znotraj platforme in je dosegljiva takoj. Lastno domeno lahko dodate pozneje, ko se prodaja ustali.</p>

<h3>Ali lahko hkrati prodajam v lastni trgovini in na kupish.si?</h3>
<p>Da. Mnogi prodajalci uporabljajo platformo kot dodaten prodajni kanal in vir novih strank, lastno trgovino pa za stalne kupce.</p>

<h3>Koliko časa potrebujem na teden?</h3>
<p>Po začetni pripravi večina prodajalcev porabi nekaj ur tedensko za posodabljanje ponudbe, odgovarjanje na povpraševanja in obdelavo naročil.</p>

<h3>Kaj potrebujem za začetek?</h3>
<p>Registriran subjekt za opravljanje dejavnosti, izdelke z osnovnimi podatki, fotografije in urejene pogoje dostave.</p>

<h2>Zaključek: najprej prodajajte, nato gradite</h2>

<p>Lastna spletna trgovina ni napačna odločitev — je le draga odločitev, ki jo je bolje sprejeti takrat, ko že veste, kaj se prodaja. Najem prostora vam omogoči, da to izveste hitreje in ceneje.</p>

<p>Če želite začeti prodajati brez tehničnih stroškov in mesecev čakanja, si oglejte pogoje na strani <a class="decorated-link" href="/najem-spletne-trgovine">Najem spletne trgovine</a>, nato pa uredite <a class="decorated-link" href="/placilo-najemnine">plačilo najemnine</a> in objavite prve izdelke. Vaša ponudba je lahko na spletu že ta teden.</p>
`,
};
